// @flow
import React from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Card,
  Divider,
  Stack,
  Switch,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import SetupGridItem from '../SetupGridItem';
import OnOffTypography from '../OnOffTypography';
import { getBooleanDisplayValue } from '../utils';
import messages from './messages';
import useIntl from '../../../hooks/useIntl';

// Passed as values: the extractintl parser does not support ICU '{...}' quoting.
export const PREVIEW_PLACEHOLDERS = {
  prospectName: '{Prospect Name}',
  propertyName: '{Property Name}',
};

type Props = {
  editMode: boolean,
  config: Object,
  isTwoWayCommunicationActive: boolean,
  showRecertTextReminders: boolean,
  onChange: Function,
  disabled?: boolean,
};

const ConfigValue = ({ name, checked, editMode, onChange, ariaLabel }) =>
  editMode ? (
    <Switch
      checked={checked}
      inputProps={{ 'aria-label': ariaLabel }}
      onChange={(event) =>
        onChange({
          target: { name: `config.${name}`, value: event.target.checked },
        })
      }
    />
  ) : (
    <OnOffTypography on={checked}>
      {getBooleanDisplayValue(checked)}
    </OnOffTypography>
  );

function TwoWayTextingSetup({
  editMode: editModeProp,
  config,
  isTwoWayCommunicationActive,
  showRecertTextReminders,
  onChange,
  disabled,
}: Props) {
  const intl = useIntl();
  const editMode = editModeProp && !disabled;
  const automatedProspectText = Boolean(config?.automatedProspectText);
  const recertTextReminders = Boolean(config?.recertTextReminders);

  return (
    <Card sx={{ padding: 2 }}>
      <Typography variant="titleLarge">
        <FormattedMessage {...messages.twoWayTexting} />
      </Typography>
      <Divider sx={{ marginY: 2 }} />
      <Grid container spacing={2}>
        <SetupGridItem
          sm={12}
          label={<FormattedMessage {...messages.twoWayTexting} />}
          TooltipProps={{
            title: <FormattedMessage {...messages.twoWayTextingTooltip} />,
          }}
        >
          <OnOffTypography on={isTwoWayCommunicationActive}>
            {getBooleanDisplayValue(isTwoWayCommunicationActive)}
          </OnOffTypography>
        </SetupGridItem>

        {isTwoWayCommunicationActive && (
          <SetupGridItem
            label={<FormattedMessage {...messages.automatedProspectText} />}
            TooltipProps={{
              title: (
                <Stack>
                  <Typography variant="bodySmall">
                    <FormattedMessage
                      {...messages.automatedProspectTextTooltip}
                    />
                  </Typography>
                  <Typography variant="bodySmall">
                    <FormattedMessage
                      {...messages.automatedProspectTextPreviewLabel}
                    />
                  </Typography>
                  <Typography variant="bodySmall">
                    <FormattedMessage
                      {...messages.automatedProspectTextPreview}
                      values={PREVIEW_PLACEHOLDERS}
                    />
                  </Typography>
                </Stack>
              ),
            }}
          >
            <ConfigValue
              name="automatedProspectText"
              checked={automatedProspectText}
              editMode={editMode}
              onChange={onChange}
              ariaLabel={intl.formatMessage(messages.automatedProspectText)}
            />
          </SetupGridItem>
        )}

        {isTwoWayCommunicationActive && showRecertTextReminders && (
          <SetupGridItem
            label={<FormattedMessage {...messages.recertTextReminders} />}
            TooltipProps={{
              title: (
                <FormattedMessage {...messages.recertTextRemindersTooltip} />
              ),
            }}
          >
            <ConfigValue
              name="recertTextReminders"
              checked={recertTextReminders}
              editMode={editMode}
              onChange={onChange}
              ariaLabel={intl.formatMessage(messages.recertTextReminders)}
            />
          </SetupGridItem>
        )}
      </Grid>
    </Card>
  );
}

export default TwoWayTextingSetup;
